import type { BarcodeIndexResponse } from "../shared/types";

const CACHE_TTL_MS = 5 * 60_000;
const DATABASE_NAME = "allthefoodispoison";
const STORE_NAME = "barcode-index";
const CACHE_KEY = "current";

interface CachedBarcodeIndex {
  key: typeof CACHE_KEY;
  checkedAt: number;
  etag: string | null;
  data: BarcodeIndexResponse;
}

export interface LoadedBarcodeIndex {
  data: BarcodeIndexResponse;
  stale: boolean;
}

let memoryCache: CachedBarcodeIndex | null = null;

function isBarcodeIndex(value: unknown): value is BarcodeIndexResponse {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  return record.version === 1
    && typeof record.generatedAt === "string"
    && Array.isArray(record.items)
    && record.items.every((item) => {
      if (typeof item !== "object" || item === null || Array.isArray(item)) return false;
      const candidate = item as Record<string, unknown>;
      return typeof candidate.id === "string"
        && typeof candidate.sourceUrl === "string"
        && (candidate.agency === "FDA" || candidate.agency === "USDA FSIS")
        && typeof candidate.title === "string"
        && (typeof candidate.company === "string" || candidate.company === null)
        && (typeof candidate.productDescription === "string" || candidate.productDescription === null)
        && (typeof candidate.reason === "string" || candidate.reason === null)
        && (typeof candidate.status === "string" || candidate.status === null)
        && typeof candidate.publishedAt === "string"
        && typeof candidate.urgent === "boolean"
        && Array.isArray(candidate.barcodes)
        && candidate.barcodes.every((barcode) => typeof barcode === "string");
    });
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open barcode cache"));
  });
}

async function readCache(): Promise<CachedBarcodeIndex | null> {
  if (!("indexedDB" in window)) return memoryCache;
  try {
    const database = await openDatabase();
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME);
      const request = transaction.objectStore(STORE_NAME).get(CACHE_KEY);
      request.onsuccess = () => {
        const value: unknown = request.result;
        if (typeof value !== "object" || value === null || Array.isArray(value)) {
          resolve(null);
          return;
        }
        const record = value as Record<string, unknown>;
        resolve(
          record.key === CACHE_KEY
          && typeof record.checkedAt === "number"
          && (typeof record.etag === "string" || record.etag === null)
          && isBarcodeIndex(record.data)
            ? {
                key: CACHE_KEY,
                checkedAt: record.checkedAt,
                etag: record.etag,
                data: record.data,
              }
            : null,
        );
      };
      request.onerror = () => {
        database.close();
        reject(request.error ?? new Error("Could not read barcode cache"));
      };
      transaction.oncomplete = () => database.close();
    });
  } catch {
    return memoryCache;
  }
}

async function writeCache(value: CachedBarcodeIndex): Promise<void> {
  memoryCache = value;
  if (!("indexedDB" in window)) return;
  try {
    const database = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).put(value);
      transaction.oncomplete = () => {
        database.close();
        resolve();
      };
      transaction.onerror = () => {
        database.close();
        reject(transaction.error ?? new Error("Could not write barcode cache"));
      };
    });
  } catch {
    // The in-memory cache still prevents duplicate requests during this page view.
  }
}

export async function loadBarcodeIndex(): Promise<LoadedBarcodeIndex> {
  const cached = await readCache();
  if (cached && Date.now() - cached.checkedAt < CACHE_TTL_MS) {
    return { data: cached.data, stale: false };
  }

  try {
    const headers = new Headers({ Accept: "application/json" });
    if (cached?.etag) headers.set("If-None-Match", cached.etag);
    const response = await fetch("/api/v1/barcodes", { headers, cache: "no-store" });
    if (response.status === 304 && cached) {
      await writeCache({ ...cached, checkedAt: Date.now() });
      return { data: cached.data, stale: false };
    }
    if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
    const data: unknown = await response.json();
    if (!isBarcodeIndex(data)) throw new Error("The recall barcode index was malformed");
    await writeCache({
      key: CACHE_KEY,
      checkedAt: Date.now(),
      etag: response.headers.get("ETag"),
      data,
    });
    return { data, stale: false };
  } catch (error) {
    if (cached) return { data: cached.data, stale: true };
    throw error;
  }
}
